import { useMemo } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import { useCreateVenuaPlannerProduct } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { buildProductFormSchema, emptySerieFormValues } from '../../serie';
import { toCreateProductRequest } from './toProductRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  ProductName: 'productName',
  SerieName: 'name',
};

interface UseProductFormOptions {
  /** Projekt, pod kterým produkt vzniká. */
  projectId: string;
  /** Voláno po úspěšném uložení. */
  onSaved: () => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na konkrétní pole (např. síťová chyba). */
  onError?: (error: unknown) => void;
}

/**
 * Zapouzdřuje formulářovou logiku produktu (produkt + první série) – TanStack Form se Zod validací,
 * create mutace a promítnutí serverových validačních chyb do polí.
 */
export function useProductForm({ projectId, onSaved, onError }: UseProductFormOptions) {
  const { t } = useTranslation('planner');
  const schema = useMemo(() => buildProductFormSchema(t), [t]);

  const createMutation = useCreateVenuaPlannerProduct();

  const form = useForm({
    defaultValues: emptySerieFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await createMutation.mutateAsync({ projectId, data: toCreateProductRequest(value) });
        onSaved();
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  return { form, isSubmitting: createMutation.isPending };
}
